import React from "react";

import { AttendanceStats } from "../types";

interface MetricCardsProps {
  data: Record<string, unknown>[];
  /** Server-computed totals over ALL rows. When present these win outright --
   *  the local window-total detection only understands detail-shaped results,
   *  and an RO-wise aggregate has no constant columns for it to find. */
  stats?: AttendanceStats | null;
}

function toNum(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

interface CardProps {
  label: string;
  value: string;
  badge?: string;
  badgeClass?: string;
  borderColor: string;
}

const MetricCard: React.FC<CardProps> = ({ label, value, badge, badgeClass, borderColor }) => (
  <div
    className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 relative overflow-hidden"
    style={{ borderTop: `3px solid ${borderColor}` }}
  >
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-2xl font-semibold text-gray-900 tabular-nums">{value}</p>
    {badge && (
      <span className={`mt-1.5 inline-block text-xs font-medium px-2 py-0.5 rounded-full ${badgeClass}`}>
        {badge}
      </span>
    )}
  </div>
);

const MetricCards: React.FC<MetricCardsProps> = ({ data, stats }) => {
  // Server stats render even with no rows: a restored chat strips the data
  // payload but keeps stats, and the chips are half the point of restoring.
  if (stats && stats.enrolled > 0) {
    const spct = (n: number) => `${((n / stats.enrolled) * 100).toFixed(1)}%`;
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Enrolled" value={stats.enrolled.toLocaleString()} borderColor="#3B82F6" />
        <MetricCard label="Present" value={stats.present.toLocaleString()}
          badge={spct(stats.present)} badgeClass="bg-green-100 text-green-700" borderColor="#10B981" />
        {stats.half_day > 0 && (
          <MetricCard label="Half Day" value={stats.half_day.toLocaleString()}
            badge={spct(stats.half_day)} badgeClass="bg-amber-100 text-amber-700" borderColor="#F59E0B" />
        )}
        <MetricCard label="Absent" value={stats.absent.toLocaleString()}
          badge={spct(stats.absent)} badgeClass="bg-red-100 text-red-700" borderColor="#EF4444" />
      </div>
    );
  }

  if (!data || data.length === 0) return null;
  const first = data[0];

  // The window-total columns arrive under different aliases depending on the
  // generated SQL (totalPresent vs totalPresentAll), and the same base name can
  // ALSO exist as a per-row column: student SQL carries sa.totalPresent (one
  // section, e.g. 28) alongside totalPresentAll (the national window total).
  // Naively preferring the plain name rendered "Present 28" beside an enrolled
  // count of 1.2M. A window total is identical on every row; a per-row value is
  // not -- so a key only qualifies if it is constant across the sample.
  const sample = data.slice(0, 50);
  const isConstant = (k: string): boolean =>
    sample.length > 0 && sample.every((row) => row[k] === sample[0][k]);
  const pick = (...keys: string[]): number | null => {
    for (const k of keys) {
      const n = toNum(first[k]);
      if (n !== null && (data.length === 1 || isConstant(k))) return n;
    }
    return null;
  };
  const enrolled = pick("totalEnrolledAll", "totalEnrollmentAll", "totalEnrolled");
  const present  = pick("totalPresentAll", "totalPresent");
  const halfDay  = pick("totalHalfDayAll", "totalHalfDay");
  const absent   = pick("totalAbsentAll", "totalAbsent");

  if (enrolled === null || present === null) return null;

  const pct = (n: number) =>
    enrolled > 0 ? `${((n / enrolled) * 100).toFixed(1)}%` : "—";

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <MetricCard
        label="Total Enrolled"
        value={enrolled.toLocaleString()}
        borderColor="#3B82F6"
      />
      <MetricCard
        label="Present"
        value={present.toLocaleString()}
        badge={pct(present)}
        badgeClass="bg-green-100 text-green-700"
        borderColor="#10B981"
      />
      {halfDay !== null && (
        <MetricCard
          label="Half Day"
          value={halfDay.toLocaleString()}
          badge={pct(halfDay)}
          badgeClass="bg-amber-100 text-amber-700"
          borderColor="#F59E0B"
        />
      )}
      {absent !== null && (
        <MetricCard
          label="Absent"
          value={absent.toLocaleString()}
          badge={pct(absent)}
          badgeClass="bg-red-100 text-red-700"
          borderColor="#EF4444"
        />
      )}
    </div>
  );
};

export default MetricCards;
